import React, {useEffect,useRef,useState} from 'react';
import backendApi from '../../services/backendApi';
import {connectDynamicsLive} from '../../services/dynamicsLive';
import {isDashboardVoiceCommand} from '../../utils/dynamicsVoiceCommands';

export default function DynamicsLiveControl({open, projectId, pageContext, onQuestion, onNavigateSource, onStatusChange, autoStartToken, autoStartPrompt, busy, awaitingCorrectionFeedback, children}) {
  const [status,setStatus]=useState('idle');
  const [error,setError]=useState('');
  const [caption,setCaption]=useState('');
  const [tokens,setTokens]=useState(0);
  const active=useRef(null), sources=useRef([]), latest=useRef(null), lastResolvedOpen=useRef(null), lastUserSpeech=useRef(''), handledNavigation=useRef(null);
  latest.current={projectId,pageContext,onQuestion,onNavigateSource,awaitingCorrectionFeedback};
  useEffect(()=>{onStatusChange?.(status);},[status,onStatusChange]);
  const stop=()=>{active.current?.abort();active.current=null;setStatus('idle');};
  useEffect(()=>{if(!open)stop(); return ()=>{active.current?.abort();};},[open,projectId]);
  const resolveVoiceNavigation=async question=>{
    const text=String(question || '').trim();
    const context=latest.current.pageContext;
    if(!text||context?.kind!=='tasks'||!(isDashboardVoiceCommand(text)||latest.current.awaitingCorrectionFeedback))return null;
    const key=text.toLowerCase().replace(/\s+/g,' ');
    const cached=handledNavigation.current;
    if(cached?.key===key)return cached.promise;
    const promise=Promise.resolve().then(()=>latest.current.onQuestion(text)).then(result=>{
      sources.current=result?.sources || [];
      lastResolvedOpen.current=result?.openSource || null;
      return result;
    });
    handledNavigation.current={key,promise};
    return promise;
  };
  const start=async(initialPrompt='')=>{
    if(active.current)return;
    const controller=new AbortController();active.current=controller;
    lastUserSpeech.current='';handledNavigation.current=null;
    setError('');setCaption('');setTokens(0);setStatus('connecting');
    try {
      const credentials=await backendApi.startDynamicsLive({projectId,pageContext});
      if(controller.signal.aborted)return;
      let role='', text='';
      await connectDynamicsLive({credentials,signal:controller.signal,initialPrompt,
        onStatus:value=>{if(!controller.signal.aborted)setStatus(value);},
        onCaption:(next,delta)=>{
          if(next==='complete'){role='';text='';return;}
          if(role!==next){role=next;text='';} text+=delta;
          if(role==='user')lastUserSpeech.current=text.trim();
          setCaption(`${role==='user'?'Vous':'Dynamix'} : ${text}`);
        },
        onUsage:usage=>setTokens(usage.totalTokenCount || 0),
        onUserTurn:()=>{handledNavigation.current=null;lastUserSpeech.current='';},
        onUserSpeech:utterance=>{if(utterance)lastUserSpeech.current=utterance;},
        onUserUtterance:async utterance=>{
          if(!isDashboardVoiceCommand(utterance)&&!latest.current.awaitingCorrectionFeedback)return;
          setStatus('thinking');
          try {await resolveVoiceNavigation(utterance);} catch { /* The assistant UI displays the request error. */ }
        },
        onTool:async call=>{
          if(call.name==='consulter_dashboard'){
            const question=call.args?.question;
            if(typeof question!=='string'||!question.trim()||question.length>500)return {error:'Question invalide.'};
            const result=(handledNavigation.current
              ? await handledNavigation.current.promise
              : await resolveVoiceNavigation(question)) || await latest.current.onQuestion(question);
            if(controller.signal.aborted)return {error:'Conversation arrêtée.'};
            sources.current=result?.sources || [];
            lastResolvedOpen.current=result?.openSource || null;
            if(result?.openSource && latest.current.pageContext?.focusedRecordId !== result.openSource.taskId) latest.current.onNavigateSource?.(result.openSource);
            return result || {error:'Attendez la fin du chargement puis réessayez.'};
          }
          if(call.name==='ouvrir_source'){
            const found=sources.current.find(s=>s.id===call.args?.sourceId);
            if(!found && lastResolvedOpen.current?.id===call.args?.sourceId)return {opened:true,alreadyOpen:true};
            if(!found) {
              const recovered=await resolveVoiceNavigation(lastUserSpeech.current);
              if(recovered?.openSource)return {opened:true,recovered:true,answer:recovered.answer};
              return {error:'Cette fiche ne figure pas dans les résultats consultés. Consultez d’abord le dashboard avec la demande complète.'};
            }
            latest.current.onNavigateSource?.(found);return {opened:true};
          }
          return {error:'Action non disponible.'};
        },
      });
    } catch(e){if(!controller.signal.aborted)setError(e.name==='NotAllowedError'?'Autorisez le microphone pour démarrer la conversation.':e.message);}
    finally {if(active.current===controller){active.current=null;setStatus('idle');}}
  };
  const startRef=useRef(start);startRef.current=start;
  useEffect(()=>{
    if (!open || !autoStartToken) return undefined;
    const timer=setTimeout(()=>startRef.current?.(autoStartPrompt || ''),350);
    return ()=>clearTimeout(timer);
  },[autoStartPrompt,autoStartToken,open]);
  const running=status!=='idle';
  const statusDetails={
    connecting:{label:'Connexion en cours…',tone:'connecting'},
    listening:{label:'Micro prêt · À votre écoute',tone:'listening'},
    speaking:{label:'Dynamix vous répond',tone:'speaking'},
    thinking:{label:'Dynamix réfléchit…',tone:'thinking'},
    expired:{label:'Session terminée',tone:'expired'},
  }[status];
  return <div className="min-w-0 w-full">
    <div className="flex items-end justify-end gap-2">
      {children}
      <button type="button" onClick={running?stop:()=>start()} disabled={!running&&(busy||!projectId||pageContext?.ready===false)} className={`dynamics-live-button dynamics-live-${status} flex h-11 w-11 shrink-0 items-center justify-center rounded-full text-white transition-colors focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[#845394] disabled:opacity-40`} aria-label={running?'Arrêter la conversation vocale':'Démarrer la conversation vocale'} title={running?'Arrêter la conversation vocale':'Conversation vocale · Le microphone sera activé après démarrage.'}>
        {status==='connecting'&&<span className="dynamics-live-spinner" aria-hidden="true" />}
        <svg width="24" height="24" viewBox="0 0 24 24" fill="none" aria-hidden="true">
          {running ? <rect x="6" y="6" width="12" height="12" rx="2" fill="currentColor" /> : <g stroke="currentColor" strokeWidth="2.4" strokeLinecap="round"><path d="M4 10v4M8 6v12M12 3v18M16 7v10M20 10v4" /></g>}
        </svg>
      </button>
    </div>
    {statusDetails && <span className={`dynamics-live-status dynamics-live-status-${statusDetails.tone}`} role="status"><span className="dynamics-live-status-dot" aria-hidden="true" />{statusDetails.label}</span>}
    {caption&&<p className="mt-2 max-h-20 overflow-y-auto text-xs text-slate-700" aria-live="polite">{caption}</p>}
    {error&&<p role="alert" className="mt-2 text-xs text-red-700">{error}</p>}
    {running && <p className="mt-2 text-[10px] text-slate-500">Micro actif · Arrêt automatique après 10 minutes.{tokens>0?` · ${tokens.toLocaleString('fr-FR')} tokens (dernier décompte reçu)`:''}</p>}
  </div>;
}
